import {
	AlertCircle,
	ArrowLeft,
	CheckCircle,
	Clock,
	Eye,
	Shield,
} from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { getApplicableTestCases } from "../data/testCaseTemplates";
import type { ProductType, TestResult, Wallet } from "../types";
import { openDocumentUrl } from "../utils/documentUrl";
import { WarningIcon } from "./icons/WarningIcon";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card } from "./ui/card";

interface TestingAdminViewProps {
	wallet: Wallet;
	isUpdating?: boolean;
	onCertify: (testResultId: string, admin_notes?: string) => void;
}

export const TestingAdminView: React.FC<TestingAdminViewProps> = ({
	wallet,
	isUpdating = false,
	onCertify,
}) => {
	const { user: _user } = useAuth();
	const [selectedResult, setSelectedResult] = useState<TestResult | null>(null);
	const [showCertifyModal, setShowCertifyModal] = useState(false);
	const [admin_notes, setAdminNotes] = useState("");

	// Derive product type from wallet products array
	const getProductType = (): ProductType | undefined => {
		if (!wallet.products || wallet.products.length === 0) return undefined;

		const hasDeposit = wallet.products.includes("Deposit");
		const hasWithdraw = wallet.products.includes("Withdraw");

		if (hasDeposit && hasWithdraw) return "both";
		if (hasDeposit) return "deposit";
		if (hasWithdraw) return "withdraw";
		return undefined;
	};

	const productType = getProductType();
	const applicableTestCases = productType
		? getApplicableTestCases(productType)
		: [];

	const getLatestResult = (test_case_id: string): TestResult | undefined => {
		return wallet.test_results
			?.filter((tr) => tr.test_case_id === test_case_id)
			.sort(
				(a, b) =>
					new Date(b.submitted_at).getTime() -
					new Date(a.submitted_at).getTime(),
			)[0];
	};

	const handleCertify = () => {
		if (selectedResult) {
			onCertify(selectedResult.id, admin_notes);
			setShowCertifyModal(false);
			setSelectedResult(null);
			setAdminNotes("");
		}
	};

	// Check if all tests are certified
	const allTestsCertified =
		applicableTestCases.length > 0 &&
		applicableTestCases.every((tc) =>
			wallet.test_results?.some(
				(tr) => tr.test_case_id === tc.id && tr.status === "certified",
			),
		);

	// Show certification modal
	if (showCertifyModal && selectedResult) {
		return (
			<div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50">
				<div className="w-[90%] max-w-[500px] rounded-sm bg-(--color-card) p-8">
					<h3 className="mt-0 text-(--color-info-600)">
						Certify Test: {selectedResult.test_case_name}
					</h3>

					<p className="mb-5 text-(--color-muted-foreground)">
						By certifying this test, you confirm that the results meet MoneyGram
						requirements.
					</p>

					<div className="space-y-2">
						<label htmlFor="admin-notes">Admin Notes (Optional)</label>
						<textarea
							id="admin-notes"
							value={admin_notes}
							onChange={(e) => setAdminNotes(e.target.value)}
							placeholder="Add any notes about the certification..."
							rows={4}
							className="w-full"
						/>
					</div>

					<div className="mt-6 flex justify-end gap-3">
						<Button
							type="button"
							variant="outline"
							onClick={() => {
								setShowCertifyModal(false);
								setAdminNotes("");
							}}
						>
							Cancel
						</Button>
						<Button type="button" onClick={handleCertify} disabled={isUpdating}>
							<Shield size={16} />
							{isUpdating ? "Certifying…" : "Certify Test"}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	// Show detailed result view
	if (selectedResult) {
		return (
			<TestResultDetailView
				result={selectedResult}
				onBack={() => setSelectedResult(null)}
				onCertify={() => setShowCertifyModal(true)}
			/>
		);
	}

	// Main admin view - list of test cases
	return (
		<div className="mt-6">
			<div className="mb-5 rounded-sm border-(--color-info-200) border-2 bg-(--color-info-50) p-5">
				<h3 className="mt-0 mb-2 text-(--color-info-600)">
					<Shield size={20} className="mr-2 inline align-middle" />
					Testing Certification (Admin View)
				</h3>
				<p className="m-0 text-(--color-muted-foreground) text-sm">
					{productType
						? "Review and certify partner test results"
						: "Wallet needs products (Deposit/Withdraw) configured to show test cases"}
				</p>
			</div>

			{!productType ? (
				<div className="rounded-sm border-2 border-warning-300 bg-warning-50 p-5 text-warning-800">
					<div className="mb-3 flex items-center gap-3">
						<WarningIcon size={24} />
						<h4 className="m-0">No Products Configured</h4>
					</div>
					<p className="m-0">
						This wallet needs to have products (Deposit and/or Withdraw)
						configured before test cases can be displayed. Please edit the
						wallet to add products.
					</p>
				</div>
			) : (
				<>
					{allTestsCertified && (
						<div className="mb-5 rounded-sm border-2 border-success-200 bg-success-50 p-4 text-success-800">
							<CheckCircle size={18} className="mr-2 inline align-middle" />
							<strong>All tests certified</strong> - Testing is complete
						</div>
					)}

					<div className="flex flex-col gap-4">
						{applicableTestCases.map((testCase) => {
							const latestResult = getLatestResult(testCase.id);

							return (
								<div
									key={testCase.id}
									className={`rounded-sm border-2 bg-(--color-card) p-5 ${
										latestResult?.status === "certified"
											? "border-success-300"
											: latestResult?.status === "submitted"
												? "border-warning-300"
												: "border-(--color-border)"
									}`}
								>
									<div className="flex items-start justify-between">
										<div className="flex-1">
											<h4 className="mt-0 mb-2 flex items-center gap-2">
												{latestResult?.status === "certified" ? (
													<CheckCircle
														size={18}
														className="text-(--color-success-500)"
													/>
												) : latestResult?.status === "submitted" ? (
													<AlertCircle
														size={18}
														className="text-(--color-warning-500)"
													/>
												) : (
													<Clock
														size={18}
														className="text-(--color-muted-foreground)"
													/>
												)}
												{testCase.name}
											</h4>

											<Badge
												variant={
													latestResult?.status === "certified"
														? "success"
														: latestResult?.status === "submitted"
															? "warning"
															: "secondary"
												}
											>
												{latestResult?.status === "certified"
													? "Certified"
													: latestResult?.status === "submitted"
														? "Awaiting Certification"
														: "Not Submitted"}
											</Badge>
										</div>

										{latestResult && (
											<Button
												type="button"
												size="sm"
												onClick={() => setSelectedResult(latestResult)}
												className="ml-3"
											>
												<Eye size={14} />
												View Results
											</Button>
										)}
									</div>

									{latestResult && (
										<div className="mt-3 rounded-xs bg-(--color-muted) p-3 text-(--color-muted-foreground) text-sm">
											Submitted on{" "}
											{new Date(latestResult.submitted_at).toLocaleString()}
											{latestResult.status === "certified" &&
												latestResult.certified_at && (
													<>
														{" "}
														• Certified on{" "}
														{new Date(
															latestResult.certified_at,
														).toLocaleString()}
													</>
												)}
										</div>
									)}
								</div>
							);
						})}
					</div>
				</>
			)}
		</div>
	);
};

// Test Result Detail View Component
interface TestResultDetailViewProps {
	result: TestResult;
	onBack: () => void;
	onCertify: () => void;
}

const TestResultDetailView: React.FC<TestResultDetailViewProps> = ({
	result,
	onBack,
	onCertify,
}) => {
	return (
		<div className="space-y-6">
			<Button type="button" variant="ghost" onClick={onBack} className="mb-5">
				<ArrowLeft size={20} />
				Back to Test Cases
			</Button>

			<Card className="border p-6">
				<div className="mb-6 flex items-start justify-between">
					<div>
						<h2 className="mt-0 mb-2 text-(--color-info-600)">
							{result.test_case_name}
						</h2>
						<Badge
							variant={result.status === "certified" ? "success" : "warning"}
						>
							{result.status === "certified"
								? "✓ Certified"
								: "Awaiting Certification"}
						</Badge>
					</div>

					{result.status === "submitted" && (
						<Button type="button" onClick={onCertify}>
							<Shield size={16} />
							Certify Test
						</Button>
					)}
				</div>

				{/* Test Results Data */}
				<div className="mb-5 rounded-xs bg-(--color-muted) p-5">
					<h3 className="mt-0 text-base">Test Results</h3>

					<div className="grid gap-3">
						{result.reference_number && (
							<div>
								<strong className="mb-1 block text-(--color-muted-foreground) text-sm">
									MoneyGram Reference Number
								</strong>
								<span>{result.reference_number}</span>
							</div>
						)}

						{result.date_of_birth && (
							<div>
								<strong className="mb-1 block text-(--color-muted-foreground) text-sm">
									Date of Birth
								</strong>
								<span>{result.date_of_birth}</span>
							</div>
						)}

						{result.transaction_status && (
							<div>
								<strong className="mb-1 block text-(--color-muted-foreground) text-sm">
									Transaction Status
								</strong>
								<span>{result.transaction_status}</span>
							</div>
						)}

						{result.amount && (
							<div>
								<strong className="mb-1 block text-(--color-muted-foreground) text-sm">
									Amount
								</strong>
								<span>{result.amount} USD</span>
							</div>
						)}

						{result.fees && (
							<div>
								<strong className="mb-1 block text-(--color-muted-foreground) text-sm">
									Fees
								</strong>
								<span>{result.fees} USD</span>
							</div>
						)}

						{result.amount_received && (
							<div>
								<strong className="mb-1 block text-(--color-muted-foreground) text-sm">
									Amount Received
								</strong>
								<span>{result.amount_received}</span>
							</div>
						)}

						{result.transaction_id && (
							<div>
								<strong className="mb-1 block text-(--color-muted-foreground) text-sm">
									Transaction ID
								</strong>
								<span className="font-mono text-sm">
									{result.transaction_id}
								</span>
							</div>
						)}
					</div>
				</div>

				{/* Screenshot */}
				{result.screenshot_url && (
					<div className="mb-5 rounded-xs bg-(--color-muted) p-5">
						<h3 className="mt-0 text-base">Screenshot</h3>
						<Button
							type="button"
							onClick={() => openDocumentUrl(result.screenshot_url ?? "")}
						>
							<Eye size={16} />
							View Screenshot
						</Button>
					</div>
				)}

				{/* Notes */}
				{result.notes && (
					<div className="mb-5 rounded-xs bg-(--color-muted) p-5">
						<h3 className="mt-0 text-base">Partner Notes</h3>
						<p className="m-0 text-(--color-foreground)">{result.notes}</p>
					</div>
				)}

				{/* Admin Notes */}
				{result.admin_notes && (
					<div className="mb-5 rounded-xs bg-(--color-info-50) p-5">
						<h3 className="mt-0 text-(--color-info-600) text-base">
							Admin Certification Notes
						</h3>
						<p className="m-0 text-(--color-foreground)">
							{result.admin_notes}
						</p>
					</div>
				)}

				{/* Metadata */}
				<div className="rounded-xs bg-(--color-muted) p-4 text-(--color-muted-foreground) text-sm">
					<div>Submitted: {new Date(result.submitted_at).toLocaleString()}</div>
					{result.certified_at && (
						<div>
							Certified: {new Date(result.certified_at).toLocaleString()}
						</div>
					)}
				</div>
			</Card>
		</div>
	);
};
